import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, processExamResults, listExamResults } from "../../api/examApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Aggregates a student's per-subject marks into one result: pass/fail,
 * average GPA, class position. Carries its own "Academic Year" selector,
 * matching the old app's own real UI — previously missing here, which
 * silently defaulted the process to the institute's *current* academic-year
 * setting instead of the year the marks actually belong to (same recurring
 * bug class as `ExamMarkEntryPage`/`ExamMarkProcessPage` elsewhere in this
 * migration).
 */
export default function ExamResultPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  const load = () => {
    if (!examConfigId) {
      setResults([]);
      return;
    }
    setLoading(true);
    listExamResults(examConfigId)
      .then(setResults)
      .catch(() => setError("Failed to load results."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [examConfigId]);

  const handleProcess = async () => {
    setProcessing(true);
    setError("");
    setSuccess("");
    try {
      const processed = await processExamResults({ examConfigId: Number(examConfigId), classConfigurationId: Number(classConfigurationId), academicYear });
      setResults(processed);
      setSuccess(`Processed ${processed.length} result(s).`);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to process results.");
    } finally {
      setProcessing(false);
    }
  };

  const columns = [
    { key: "classPosition", label: "Position", render: (row) => (row.classPosition === 0 ? "—" : row.classPosition) },
    { key: "studentRoll", label: "Roll" },
    { key: "studentName", label: "Student" },
    { key: "obtainMark", label: "Obtained" },
    { key: "totalMark", label: "Total" },
    { key: "letterGrade", label: "Grade" },
    { key: "avgGpaWithoutOptional", label: "GPA" },
    { key: "resultStatus", label: "Status" },
    { key: "failedSubject", label: "Failed Subjects" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Result Process</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-secondary" onClick={handleProcess} disabled={processing || !examConfigId}>
          {processing ? "Processing..." : "Process Results"}
        </button>
      </div>

      {loading ? <p>Loading...</p> : <DataTable columns={columns} rows={results} />}
    </MainLayout>
  );
}
